import {
	StatusEnum,
	useDeleteManyOrganizationUserMutation,
	useDeleteOrganizationUserMutation,
	useGetAllClientContactQuery,
	useInviteOrganizationUserMutation,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { Contact, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { OrganizationSelector } from '../../components/selectors/OrganizationSelector';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

import { EmptyState } from '../../components/ui/empty-state';
import { TableSkeleton } from '../../components/ui/list-skeletons';
import { SearchInput } from '../../components/ui/search-input';
import { SelectionBar } from '../../components/ui/selection-bar';

import { Tabs } from '../../components/ui/tabs';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import {
	ContactsTable,
	contactFullName as fullName,
	type ClientContact,
} from '../../components/contacts/ContactsTable';
import { timeAgo } from '../../lib/utils';
import { AddContactModal } from '../clients/AddContactModal';
import { EditContactModal, type ContactData } from './EditContactModal';

/**
 * The list buckets. They deliberately overlap — a guest can also be PENDING —
 * so these are filters, not a partition, and their counts don't sum to `all`.
 */
const TABS = ['all', 'active', 'invited', 'guests'] as const;
type ContactTab = (typeof TABS)[number];

/** Server-side filter for a bucket, on top of whatever search/client filter is active. */
function tabFilter(tab: ContactTab): { status?: StatusEnum; isGuest?: boolean } {
	switch (tab) {
		case 'active':
			return { status: StatusEnum.Completed };
		case 'invited':
			return { status: StatusEnum.Pending };
		case 'guests':
			return { isGuest: true };
		case 'all':
			return {};
	}
}

/**
 * Bucket counts for the tabs and KPI strip.
 *
 * There is no overview endpoint for client contacts, so each count is a real
 * `totalCount` from the server rather than a tally of the current page — which
 * would only ever count the 20 rows in view. `limit: 1` keeps the payload tiny.
 */
function useContactCounts(search: string, organizationId?: string) {
	const base = {
		pagination: { limit: 1, currentPage: 1 },
		search: search || undefined,
		organizationId: organizationId || undefined,
	};
	const options = { fetchPolicy: 'no-cache' as const };

	const all = useGetAllClientContactQuery({ variables: { input: base }, ...options });
	const active = useGetAllClientContactQuery({
		variables: { input: { ...base, status: StatusEnum.Completed } },
		...options,
	});
	const invited = useGetAllClientContactQuery({
		variables: { input: { ...base, status: StatusEnum.Pending } },
		...options,
	});
	const guests = useGetAllClientContactQuery({
		variables: { input: { ...base, isGuest: true } },
		...options,
	});

	return {
		all: all.data?.getAllClientContact?.pageInfo?.totalCount,
		active: active.data?.getAllClientContact?.pageInfo?.totalCount,
		invited: invited.data?.getAllClientContact?.pageInfo?.totalCount,
		guests: guests.data?.getAllClientContact?.pageInfo?.totalCount,
		refetch: () => {
			all.refetch();
			active.refetch();
			invited.refetch();
			guests.refetch();
		},
	};
}

/**
 * One row of ContactsTable, for sizing scroll-mode batches: the name cell's 13px name over a
 * 12px job title (at the default 1.5 line height, just taller than the 32px avatar beside it)
 * plus cell padding and the row border.
 */
function contactRows(): RowEstimate {
	return { rowHeight: 59 };
}

/* ════════════════════════ CONTACTS PAGE ════════════════════════ */

export function ContactsPage() {
	useSetPageMeta('Contacts', [{ label: 'Contacts' }]);

	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({ defaultSort: 'firstName', defaultDir: 'asc', scrollRows: contactRows });

	const [tabParam, setTab] = useQueryParam('tab', 'all');
	const tab = (TABS as readonly string[]).includes(tabParam) ? (tabParam as ContactTab) : 'all';
	const [clientId, setClientId] = useQueryParam('client', '');

	const [addModalOpen, setAddModalOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<ContactData | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

	const { data, loading, refetch } = useGetAllClientContactQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				organizationId: clientId || undefined,
				...tabFilter(tab),
			},
		},
		fetchPolicy: 'no-cache',
	});

	/* The contacts query types `data` and `pageInfo` as optional. Memoised, as
	   usePagedList tells a new result by identity. */
	const contactsPage = useMemo(() => {
		const result = data?.getAllClientContact;
		return result?.pageInfo && { data: result.data ?? [], pageInfo: result.pageInfo };
	}, [data]);
	const list = usePagedList({ mode, request, limit, setPage, loadMore }, contactsPage, loading);
	const contacts = list.items as ClientContact[];

	const counts = useContactCounts(debouncedSearch, clientId || undefined);

	const refetchAll = () => {
		reload(refetch);
		counts.refetch();
	};

	/* ── Selection state ── */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const contactIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
	const allSelected = contactIds.length > 0 && contactIds.every((id) => selectedIds.has(id));

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelectedIds(allSelected ? new Set() : new Set(contactIds));
	};

	// Clear selection whenever the visible set is replaced. Scrolling keeps earlier rows on
	// screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelectedIds(new Set());
	}, [shownPage, mode, debouncedSearch, tab, clientId]);

	/* ── Delete ── */
	const [deleteOrganizationUser, { loading: deleting }] = useDeleteOrganizationUserMutation();
	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteOrganizationUser({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Contact removed');
			setDeleteTarget(null);
			refetchAll();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove contact');
		}
	};

	const [deleteManyOrganizationUser, { loading: bulkDeleting }] =
		useDeleteManyOrganizationUserMutation();
	const handleBulkDelete = async () => {
		try {
			await deleteManyOrganizationUser({
				variables: { input: { ids: Array.from(selectedIds) } },
			});
			toast.success(`${selectedIds.size} contact${selectedIds.size > 1 ? 's' : ''} removed`);
			setSelectedIds(new Set());
			setBulkDeleteOpen(false);
			refetchAll();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove contacts');
		}
	};

	/**
	 * Re-send is the same call as the first send; the backend refuses it once the
	 * contact is COMPLETED, since an active account has nothing left to accept.
	 */
	const [inviteOrganizationUser, { loading: inviting }] = useInviteOrganizationUserMutation();
	const [inviteTarget, setInviteTarget] = useState<ClientContact | null>(null);
	const handleInvite = async () => {
		const c = inviteTarget;
		if (!c) return;
		try {
			const { data } = await inviteOrganizationUser({ variables: { input: { id: c.id } } });
			if (data?.inviteOrganizationUser?.success) {
				toast.success(
					c.invitedAt
						? `Invitation re-sent to ${fullName(c)}`
						: `Invitation sent to ${fullName(c)}`
				);
				// invitedAt just changed, and the badge and the menu label both read
				// it — without this the row still says "Not invited".
				reload(refetch);
			} else {
				toast.error(data?.inviteOrganizationUser?.message || 'Failed to send invitation');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
		}
		setInviteTarget(null);
	};

	const openEdit = (c: ClientContact) => {
		setEditTarget({
			id: c.id,
			firstName: c.firstName,
			lastName: c.lastName,
			email: c.authenticationUser?.email,
			jobPosition: c.jobPosition,
			phone: c.phone,
			birthday: c.birthday,
			notes: c.notes,
			organizationName: c.organization?.name,
		});
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Contacts
					</h1>
					<p className="text-base text-text-3">
						Every person you work with across your client organizations.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setAddModalOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						Add contact
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken lg:grid-cols-4">
				{[
					{ label: 'Total contacts', value: counts.all, sub: 'Across all clients' },
					{ label: 'Active', value: counts.active, sub: 'Onboarding completed' },
					{ label: 'Pending', value: counts.invited, sub: 'Not activated yet' },
					{ label: 'Guests', value: counts.guests, sub: 'From acceptance links' },
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value ?? '—'}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All contacts', count: counts.all },
						{ value: 'active', label: 'Active', count: counts.active },
						{ value: 'invited', label: 'Pending', count: counts.invited },
						{ value: 'guests', label: 'Guests', count: counts.guests },
					]}
				/>
				<div className="flex items-center gap-2">
					<OrganizationSelector
						value={clientId}
						onChange={(v) => {
							setClientId(v);
							setPage(1);
						}}
						placeholder="All clients"
						variant="toolbar"
						clearable
						className="w-[210px]"
					/>
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search contacts…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{list.loading ? (
				<TableSkeleton />
			) : contacts.length === 0 ? (
				<EmptyState
					icon={Contact}
					title="No contacts found"
					description={
						search || clientId || tab !== 'all'
							? 'Try a different search or filter.'
							: 'Contacts appear here once clients invite their team.'
					}
				/>
			) : (
				<ContactsTable
					contacts={contacts}
					sortField={sortField}
					sortDir={sortDir}
					onSort={toggleSort}
					selectedIds={selectedIds}
					onToggleOne={toggleOne}
					onToggleAll={toggleAll}
					inviting={inviting}
					onInvite={setInviteTarget}
					onEdit={openEdit}
					onDelete={(c) => setDeleteTarget({ id: c.id, name: fullName(c) })}
				/>
			)}

			<SelectionBar
				count={selectedIds.size}
				actions={[
					{ label: 'Remove', icon: Trash2, onClick: () => setBulkDeleteOpen(true) },
				]}
				onClear={() => setSelectedIds(new Set())}
			/>

			<ListFooter list={list} />

			<AddContactModal
				open={addModalOpen}
				onOpenChange={setAddModalOpen}
				onSuccess={refetchAll}
			/>

			<EditContactModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				contact={editTarget}
				onSuccess={refetchAll}
			/>

			{/*
			 * Re-sending is not idempotent from the recipient's side — it emails
			 * them again, and the earlier invitation link stays valid — so the
			 * copy says which of the two is about to happen.
			 */}
			<ConfirmDialog
				open={!!inviteTarget}
				onOpenChange={(open) => {
					if (!open) setInviteTarget(null);
				}}
				title={inviteTarget?.invitedAt ? 'Re-send invitation' : 'Send invitation'}
				description={
					inviteTarget?.invitedAt
						? `${inviteTarget ? fullName(inviteTarget) : ''} was already emailed an invitation ${timeAgo(inviteTarget.invitedAt)}. Sending another emails them again, and the earlier invitation link stays valid.`
						: `${inviteTarget ? fullName(inviteTarget) : ''} will be emailed an invitation to set up their account.`
				}
				confirmLabel={inviteTarget?.invitedAt ? 'Re-send' : 'Send invitation'}
				onConfirm={handleInvite}
				loading={inviting}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Remove contact"
				description={`Are you sure you want to remove ${deleteTarget?.name}? They will lose access to their organization.`}
				variant="destructive"
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={bulkDeleteOpen}
				onOpenChange={setBulkDeleteOpen}
				title="Remove contacts"
				description={`Are you sure you want to remove ${selectedIds.size} contact${selectedIds.size > 1 ? 's' : ''}? They will lose access to their organizations.`}
				variant="destructive"
				onConfirm={handleBulkDelete}
				loading={bulkDeleting}
			/>
		</div>
	);
}
